/**
 * Popis karty do bublinky vedľa nej.
 *
 * Karta samotná zostáva pôvodná — toto je vysvetlenie, nie náhrada: čo tá
 * hodnota vlastne znamená (útok? obrana? oboje?), kedy sa karta zahráva,
 * kto ju smie zahrať a čo urobí. Texty efektov idú z obsahu, teda zatiaľ
 * po anglicky; návestia okolo nich sú súčasť UI, a tá je po slovensky.
 */

import type { CardDef } from '@unmatched/engine';
import { fighterLabel, localizeCard } from '@unmatched/content';
import { EFFECTS } from '@unmatched/engine';

import { useCardDisplay } from './cardDisplay.js';

const TYPE_INFO: Record<CardDef['type'], { label: string; note: string; value: string }> = {
  attack: {
    label: 'Útok',
    note: 'Zahráš ju, keď útočíš ty.',
    value: 'sila útoku',
  },
  defense: {
    label: 'Obrana',
    note: 'Zahráš ju, keď útočia na teba.',
    value: 'sila obrany',
  },
  versatile: {
    label: 'Univerzál',
    note: 'Dá sa zahrať ako útok aj ako obrana.',
    value: 'sila útoku aj obrany',
  },
  scheme: {
    label: 'Plán',
    note: 'Zahráva sa ako akcia mimo súboja.',
    value: 'hodnota karty',
  },
};

const PHASE_LABEL = {
  basic: null,
  immediate: 'IMMEDIATELY',
  during: 'DURING COMBAT',
  after: 'AFTER COMBAT',
} as const;

const PHASE_NOTE: Record<'immediate' | 'during' | 'after', string> = {
  immediate: 'vyhodnotí sa hneď po odhalení kariet',
  during: 'platí počas súboja, ešte pred poškodením',
  after: 'vyhodnotí sa až po rozdaní poškodenia',
};

export function CardInfo({ def, valueBonus = 0 }: { def: CardDef; valueBonus?: number }) {
  const { locale } = useCardDisplay();
  const text = localizeCard(def, locale);
  const type = TYPE_INFO[def.type];
  const banner = fighterLabel(def.character);
  const phases = (['basic', 'immediate', 'during', 'after'] as const).filter((p) => text.effects[p]);
  // Efekty má hotové len časť postáv — pri zvyšku je text zatiaľ dekorácia.
  const implemented = Boolean(EFFECTS[def.id]);

  return (
    <div className={`cardInfo cardInfo--${def.type}`}>
      <header className="cardInfo__head">
        <span className="cardInfo__type">{type.label}</span>
        <h3 className="cardInfo__name">{text.name}</h3>
      </header>

      <p className="cardInfo__note">{type.note}</p>

      <div className="cardInfo__stats">
        <span className={`cardInfo__stat${valueBonus > 0 ? ' is-boosted' : ''}`}>
          <b>{def.value + valueBonus}</b> {type.value}
        </span>
        <span className="cardInfo__stat">
          <b>{def.boost}</b> boost
        </span>
      </div>

      {valueBonus > 0 ? (
        <p className="cardInfo__note cardInfo__note--boost">
          Na karte je vytlačené <b>{def.value}</b>, ale efekt jej práve teraz pridáva{' '}
          <b>+{valueBonus}</b>.
        </p>
      ) : null}

      <p className="cardInfo__note">
        {def.boost > 0 ? (
          <>
            Pri manévri ju smieš odhodiť za <b>+{def.boost}</b> k pohybu.
          </>
        ) : (
          <>Na boost je nanič — má nulu.</>
        )}
      </p>

      <p className="cardInfo__banner">
        Smie zahrať: <b>{banner ?? 'ktorákoľvek tvoja figúrka'}</b>
      </p>

      {phases.length > 0 ? (
        <div className="cardInfo__effects">
          {phases.map((phase) => (
            <div key={phase} className="cardInfo__effect">
              {PHASE_LABEL[phase] ? (
                <span className="cardInfo__phase">
                  {PHASE_LABEL[phase]}
                  <em>{PHASE_NOTE[phase as 'immediate' | 'during' | 'after']}</em>
                </span>
              ) : null}
              <p>{text.effects[phase]}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="cardInfo__note">Bez textu — rozhoduje len hodnota.</p>
      )}

      <footer className="cardInfo__foot">
        <span>
          {def.qty}× v balíčku
        </span>
        {phases.length > 0 ? (
          implemented ? (
            <span className="cardInfo__ok">efekt funguje</span>
          ) : (
            <span className="cardInfo__warn">efekt zatiaľ nefunguje</span>
          )
        ) : null}
      </footer>
    </div>
  );
}
